import React, { useState } from "react";
import { Calculator, Home, Landmark, Shield } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import Seo from "../components/Seo";

const fmt = (n) => `£${Math.round(n).toLocaleString()}`;

// UK SDLT (standard residential, single main residence) — simplified 2026 bands
const calcStampDuty = (price) => {
  const bands = [
    { upTo: 250000, rate: 0 },
    { upTo: 925000, rate: 0.05 },
    { upTo: 1500000, rate: 0.10 },
    { upTo: Infinity, rate: 0.12 },
  ];
  let tax = 0;
  let last = 0;
  for (const b of bands) {
    if (price > last) {
      const taxable = Math.min(price, b.upTo) - last;
      tax += taxable * b.rate;
      last = b.upTo;
    } else break;
  }
  return tax;
};

const StampDuty = () => {
  const [price, setPrice] = useState(335000);
  const tax = calcStampDuty(price);
  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      <div className="space-y-4">
        <label className="block text-[13px] text-[#6a6a6a] mb-1">Property price</label>
        <input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)} className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
        <input type="range" min="100000" max="2000000" step="5000" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-full accent-[#b8935f]" />
      </div>
      <div className="bg-[#faf7f2] rounded-xl p-7 border border-[#f0ece5]">
        <p className="text-[12px] tracking-[0.15em] uppercase text-[#b8935f] mb-2">Estimated Stamp Duty</p>
        <p className="font-display text-[38px] font-500 text-[#2b2b2b]">{fmt(tax)}</p>
        <p className="text-[13px] text-[#8a8a8a] mt-2">Effective rate: {((tax / price) * 100 || 0).toFixed(2)}%</p>
        <p className="text-[12px] text-[#a0a0a0] mt-4">Based on standard residential rates for a single main home. Rates differ for additional properties and first-time buyers.</p>
      </div>
    </div>
  );
};

const Mortgage = () => {
  const [price, setPrice] = useState(335000);
  const [deposit, setDeposit] = useState(50000);
  const [rate, setRate] = useState(4.5);
  const [years, setYears] = useState(25);
  const loan = Math.max(0, price - deposit);
  const r = rate / 100 / 12;
  const n = years * 12;
  const monthly = r === 0 ? loan / n : (loan * r) / (1 - Math.pow(1 + r, -n));
  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      <div className="space-y-4">
        {[["Property price", price, setPrice, 5000], ["Deposit", deposit, setDeposit, 1000]].map(([label, val, set, step]) => (
          <div key={label}>
            <label className="block text-[13px] text-[#6a6a6a] mb-1">{label}</label>
            <input type="number" value={val} step={step} onChange={(e) => set(Number(e.target.value) || 0)} className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] text-[#6a6a6a] mb-1">Interest rate (%)</label>
            <input type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value) || 0)} className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          </div>
          <div>
            <label className="block text-[13px] text-[#6a6a6a] mb-1">Term (years)</label>
            <input type="number" value={years} onChange={(e) => setYears(Number(e.target.value) || 1)} className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          </div>
        </div>
      </div>
      <div className="bg-[#faf7f2] rounded-xl p-7 border border-[#f0ece5]">
        <p className="text-[12px] tracking-[0.15em] uppercase text-[#b8935f] mb-2">Monthly Repayment</p>
        <p className="font-display text-[38px] font-500 text-[#2b2b2b]">{fmt(monthly || 0)}</p>
        <div className="mt-4 space-y-2 text-[13px] text-[#6a6a6a]">
          <div className="flex justify-between"><span>Loan amount</span><span className="text-[#2b2b2b]">{fmt(loan)}</span></div>
          <div className="flex justify-between"><span>Total repayable</span><span className="text-[#2b2b2b]">{fmt((monthly || 0) * n)}</span></div>
          <div className="flex justify-between"><span>Total interest</span><span className="text-[#2b2b2b]">{fmt(Math.max(0, (monthly || 0) * n - loan))}</span></div>
        </div>
      </div>
    </div>
  );
};

const Insurance = () => {
  const [rebuild, setRebuild] = useState(280000);
  const [contents, setContents] = useState(40000);
  const annual = rebuild * 0.0012 + contents * 0.004 + 90;
  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      <div className="space-y-4">
        {[["Rebuild value", rebuild, setRebuild], ["Contents value", contents, setContents]].map(([label, val, set]) => (
          <div key={label}>
            <label className="block text-[13px] text-[#6a6a6a] mb-1">{label}</label>
            <input type="number" step="1000" value={val} onChange={(e) => set(Number(e.target.value) || 0)} className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          </div>
        ))}
      </div>
      <div className="bg-[#faf7f2] rounded-xl p-7 border border-[#f0ece5]">
        <p className="text-[12px] tracking-[0.15em] uppercase text-[#b8935f] mb-2">Estimated Annual Premium</p>
        <p className="font-display text-[38px] font-500 text-[#2b2b2b]">{fmt(annual)}</p>
        <p className="text-[13px] text-[#8a8a8a] mt-2">~{fmt(annual / 12)} per month</p>
        <p className="text-[12px] text-[#a0a0a0] mt-4">Indicative buildings &amp; contents estimate. Actual premiums vary by insurer, location and cover level.</p>
      </div>
    </div>
  );
};

const Calculators = () => {
  const tabs = [
    { id: "stampduty", label: "Stamp Duty", icon: Landmark, comp: <StampDuty /> },
    { id: "mortgage", label: "Mortgage", icon: Home, comp: <Mortgage /> },
    { id: "insurance", label: "Insurance", icon: Shield, comp: <Insurance /> },
  ];
  return (
    <div className="min-h-[70vh]">
      <Seo title="Mortgage, Stamp Duty & Property Calculators" description="Free property calculators from Anadear Estates: mortgage repayments, stamp duty and insurance for buyers and investors in Bath." path="/calculators" />
      <div className="bg-[#1c1c1e] py-16">
        <div className="max-w-[1080px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2 flex items-center gap-2"><Calculator size={15} /> Property Tools</p>
          <h1 className="font-display text-white text-[36px] md:text-[46px] font-300">Calculators</h1>
          <p className="text-white/60 text-[14px] mt-3 max-w-xl">Plan your move with our suite of property calculators — stamp duty, mortgage repayments and insurance.</p>
        </div>
      </div>
      <div className="max-w-[1080px] mx-auto px-5 md:px-8 py-14">
        <Tabs defaultValue="stampduty">
          <TabsList className="grid grid-cols-3 gap-2 h-auto bg-[#faf7f2] p-2 mb-10">
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:bg-[#b8935f] data-[state=active]:text-white py-3 flex items-center gap-2">
                <t.icon size={16} /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.id} value={t.id}>{t.comp}</TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default Calculators;
